import { request, maybeMock } from "./http";
import type { Consent, Message, Paginated, QuestLog, Story, User } from "./types";

// Me / profile

export function getMe(): Promise<User> {
  return request<User>("/me", { auth: true });
}

export function patchMe(body: Partial<User>): Promise<User> {
  return request<User>("/me", { method: "PATCH", body, auth: true });
}

// Partner links

export function partnersInitiate(invitee_public_id?: string): Promise<{
  ok?: boolean;
  code?: string;
  link?: {
    id?: string;
    status?: string;
    code?: string | null;
    invitee_public_id?: string | null;
  };
}> {
  return request("/partners/link/initiate", {
    method: "POST",
    body: invitee_public_id ? { invitee_public_id } : undefined,
    auth: true
  });
}

export function partnersAccept(code: string): Promise<{ ok: boolean; link?: { id?: string; status?: string } }> {
  return request("/partners/link/accept", {
    method: "POST",
    body: { code },
    auth: true
  });
}

export function partnersList(): Promise<{
  ok?: boolean;
  links: any[];
  accepted?: any[];
  incoming?: any[];
  outgoing?: any[];
}> {
  return request("/partners", { auth: true });
}

export function partnersUnlink(id: string): Promise<{ ok: boolean }> {
  return request("/partners/unlink", {
    method: "POST",
    body: { id },
    auth: true
  });
}

// Messages

export function listMessages(
  partnerId: string,
  cursor?: string
): Promise<Paginated<Message>> {
  const qs = new URLSearchParams({ partnerId });
  if (cursor) qs.set("cursor", cursor);
  return request<Paginated<Message>>(`/messages?${qs.toString()}`, {
    auth: true
  });
}

export function sendMessage(
  toId: string,
  body: string
): Promise<Message> {
  return request<Message>("/messages", {
    method: "POST",
    body: { toId, body },
    auth: true
  });
}

// Consents

export function listConsents(
  status?: string
): Promise<Paginated<Consent>> {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  const suffix = qs.toString();
  return request<Paginated<Consent>>(
    `/consents${suffix ? `?${suffix}` : ""}`,
    { auth: true }
  );
}

export function createConsent(
  questId: string,
  partnerId: string,
  notes?: string
): Promise<Consent> {
  return request<Consent>("/consents", {
    method: "POST",
    body: { questId, partnerId, notes },
    auth: true
  });
}

export function updateConsent(
  id: string,
  patch: Partial<Consent>
): Promise<Consent> {
  return request<Consent>(`/consents/${id}`, {
    method: "PATCH",
    body: patch,
    auth: true
  });
}

// Quest logs

export function listQuestLogs(query?: {
  from?: string;
  to?: string;
  category?: string;
}): Promise<Paginated<QuestLog>> {
  const qs = new URLSearchParams();
  if (query?.from) qs.set("from", query.from);
  if (query?.to) qs.set("to", query.to);
  if (query?.category) qs.set("category", query.category);
  const suffix = qs.toString();
  return request<Paginated<QuestLog>>(
    `/quests/logs${suffix ? `?${suffix}` : ""}`,
    { auth: true }
  );
}

export function createQuestLog(
  payload: Partial<QuestLog>
): Promise<QuestLog> {
  return request<QuestLog>("/quests/logs", {
    method: "POST",
    body: payload,
    auth: true
  });
}


// Quest home / runs

export type QuestRun = {
  id?: string | number;
  questId?: string | number;
  quest_id?: string | number;
  mode?: string | null;
  status?: string | null;
  style?: string | null;
  variation?: string | null;
  clientRunId?: string | null;
  createdBy?: string | number | null;
  acceptedBy?: string | number | null;
  isCreator?: boolean;
  actionRequired?: boolean;
  partnerLinkId?: string | number | null;
  partner?: any;
  quest?: any;
  createdAt?: string | null;
  updatedAt?: string | null;
  respondedAt?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
};

export type QuestHomeSummary = {
  ok?: boolean;
  user?: any;
  soloPack?: any;
  relationshipPack?: any;
  partner?: any;
  activeRun?: QuestRun | null;
  pendingProposal?: QuestRun | null;
  recentCompletion?: QuestRun | null;
  recommendedQuest?: any | null;
  dailyObjective?: any | null;
};

export function getQuestHome(): Promise<QuestHomeSummary> {
  return request<QuestHomeSummary>("/quests/home", { auth: true });
}

export function createQuestRun(
  questId: string | number,
  payload: Record<string, any> = {}
): Promise<{ ok?: boolean; item?: QuestRun; run?: QuestRun }> {
  return request(`/quests/${encodeURIComponent(String(questId))}/runs`, {
    method: "POST",
    body: payload,
    auth: true
  });
}

export function getQuestRun(
  runId: string | number
): Promise<{ ok?: boolean; item?: QuestRun; run?: QuestRun }> {
  return request(`/quest-runs/${encodeURIComponent(String(runId))}`, {
    auth: true
  });
}

export function respondQuestRun(
  runId: string | number,
  payload: Record<string, any>
): Promise<{ ok?: boolean; item?: QuestRun; run?: QuestRun }> {
  return request(`/quest-runs/${encodeURIComponent(String(runId))}/respond`, {
    method: "POST",
    body: payload,
    auth: true
  });
}

export function startQuestRun(
  runId: string | number
): Promise<{ ok?: boolean; item?: QuestRun; run?: QuestRun }> {
  return request(`/quest-runs/${encodeURIComponent(String(runId))}/start`, {
    method: "POST",
    body: {},
    auth: true
  });
}

export function completeQuestRun(
  runId: string | number,
  payload: Record<string, any> = {}
): Promise<{ ok?: boolean; item?: QuestRun; run?: QuestRun }> {
  return request(`/quest-runs/${encodeURIComponent(String(runId))}/complete`, {
    method: "POST",
    body: payload,
    auth: true
  });
}

export function abortQuestRun(
  runId: string | number
): Promise<{ ok?: boolean; item?: QuestRun; run?: QuestRun }> {
  return request(`/quest-runs/${encodeURIComponent(String(runId))}/abort`, {
    method: "POST",
    body: {},
    auth: true
  });
}

export function cancelQuestRun(
  runId: string | number
): Promise<{ ok?: boolean; item?: QuestRun; run?: QuestRun }> {
  return request(`/quest-runs/${encodeURIComponent(String(runId))}/cancel`, {
    method: "POST",
    body: {},
    auth: true
  });
}


// Stories

export function listStories(
  scope: "public" | "mine" | "partner" = "public"
): Promise<Paginated<Story>> {
  return maybeMock(
    "listStories",
    () => request<Paginated<Story>>(`/stories?scope=${scope}`, { auth: true }),
    {
      items: [
        {
          id: "welcome",
          title: "Welcome to IntimSync",
          content:
            "This is a placeholder story. Once moderation is live, you will see approved community stories here.",
          visibility: "PUBLIC",
          status: "APPROVED",
          createdAt: new Date().toISOString()
        }
      ],
      cursor: null,
      hasMore: false
    }
  );
}

export function getStory(id: string): Promise<Story> {
  return maybeMock(
    "getStory",
    () => request<Story>(`/stories/${id}`, { auth: true }),
    {
      id,
      title: "Story",
      content: "This is a mock story. Replace with live content from the API.",
      visibility: "PUBLIC",
      status: "APPROVED",
      createdAt: new Date().toISOString()
    }
  );
}

export function createStory(
  story: Partial<Story>
): Promise<Story> {
  return request<Story>("/stories", {
    method: "POST",
    body: story,
    auth: true
  });
}

export function patchStory(
  id: string,
  patch: Partial<Story>
): Promise<Story> {
  return request<Story>(`/stories/${id}`, {
    method: "PATCH",
    body: patch,
    auth: true
  });
}

export function publishStory(id: string): Promise<Story> {
  return request<Story>(`/stories/${id}/publish`, {
    method: "POST",
    auth: true
  });
}

// Features / entitlements

export type Features = Record<string, any>;
export type Entitlements = Record<string, any>;

export function fetchFeatures(): Promise<Features> {
  return request<Features>("/features", { auth: true });
}

export function fetchEntitlements(): Promise<Entitlements> {
  return request<Entitlements>("/entitlements", { auth: true });
}


export function partnersRevoke(id: string): Promise<{ ok: boolean; id?: string; status?: string }> {
  return request("/partners/link/revoke", {
    method: "POST",
    body: { id },
    auth: true
  });
}


export function partnersConfirm(id: string): Promise<{ ok: boolean; link?: { id?: string; status?: string } }> {
  return request("/partners/link/confirm", {
    method: "POST",
    body: { id },
    auth: true
  });
}
